import type { ReactNode } from "react";

import { useTablistKeyboardNavigation } from "../TabSelector/useTablistKeyboardNavigation";

import styles from "./SegmentedControl.module.css";

/**
 * A single segment descriptor. `id` is the value passed back to
 * `onChange`; the generic parameter on `SegmentedControlProps` keeps
 * this type-safe for callers that use a string-literal union
 * (e.g. `"FREE" | "PREMIUM"`).
 */
export type SegmentedControlItem<T extends string = string> = {
  id: T;
  label: ReactNode;
  /** Disabled segments are still rendered, just non-interactive. */
  disabled?: boolean;
  /** Optional per-segment test id suffix; falls back to `id` when omitted. */
  testId?: string;
};

export type SegmentedControlProps<T extends string = string> = {
  items: ReadonlyArray<SegmentedControlItem<T>>;
  /** Controlled active segment id. */
  value: T;
  onChange: (next: T) => void;
  /** Accessible name for the group (announced by screen readers). */
  ariaLabel?: string;
  className?: string;
  /** Root data-testid; per-segment buttons get
   *  `${dataTestId}-${item.testId|item.id}`. */
  "data-testid"?: string;
};

/**
 * Pill-style segmented control — a horizontal "track" with one active
 * brand-colored pill on top of an inactive bed. Sibling to
 * `TabSelector`: same controlled-only API and shared
 * `useTablistKeyboardNavigation` for keyboard parity, different visual
 * (segmented pills instead of an underline strip).
 *
 * Use `TabSelector` when the control reads as page-level navigation
 * (long underline strip spanning the panel). Use `SegmentedControl`
 * when the control reads as an in-card filter / mode toggle — e.g. the
 * Free / Premium tier switch on Game Pass → Season Details.
 *
 * Reuses the WAI-ARIA "tablist" pattern so the same hook drives both
 * components:
 *   - `Tab` moves focus into / out of the group (only the active
 *     segment is in the tab sequence; inactive segments have
 *     `tabIndex={-1}`).
 *   - `ArrowLeft` / `ArrowRight` move to the previous / next enabled
 *     segment, wrapping at the ends.
 *   - `Home` / `End` jump to the first / last enabled segment.
 *   - Disabled segments are skipped.
 */
function SegmentedControl<T extends string = string>({
  items,
  value,
  onChange,
  ariaLabel,
  className = "",
  "data-testid": dataTestId,
}: SegmentedControlProps<T>) {
  const { tabRefs, handleKeyDown } = useTablistKeyboardNavigation<T>({
    items,
    value,
    onChange,
  });

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${styles.root}${className ? ` ${className}` : ""}`}
      data-testid={dataTestId}
    >
      {items.map((item, index) => {
        const isActive = item.id === value;
        const segmentClassName = `${styles.segment}${
          isActive ? ` ${styles.segmentActive}` : ""
        }`;
        const segmentTestId = dataTestId
          ? `${dataTestId}-${item.testId ?? item.id}`
          : undefined;
        return (
          <button
            key={item.id}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={item.disabled || undefined}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            className={segmentClassName}
            onClick={() => {
              if (item.disabled || isActive) return;
              onChange(item.id);
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={segmentTestId}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
